import { useState, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { CustomerFormData, PartnerFormData, CemeteryFormData } from "../types";
import { INITIAL_CUSTOMER_FORM, INITIAL_PARTNER_FORM, INITIAL_CEMETERY_FORM } from "../types";

interface UseInlineCreationProps {
  onCustomerCreated: (id: string) => void;
  onPartnerCreated: (id: string) => void;
  onCemeteryCreated: (id: string) => void;
}

export function useInlineCreation({
  onCustomerCreated,
  onPartnerCreated,
  onCemeteryCreated,
}: UseInlineCreationProps) {
  const queryClient = useQueryClient();

  // Visibility states
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [showCemeteryForm, setShowCemeteryForm] = useState(false);

  // Form states
  const [customerForm, setCustomerForm] = useState<CustomerFormData>(INITIAL_CUSTOMER_FORM);
  const [partnerForm, setPartnerForm] = useState<PartnerFormData>(INITIAL_PARTNER_FORM);
  const [cemeteryForm, setCemeteryForm] = useState<CemeteryFormData>(INITIAL_CEMETERY_FORM);

  // Customer mutation
  const customerMutation = useMutation({
    mutationFn: async (data: CustomerFormData) => {
      const { data: customer, error } = await supabase
        .from("customers")
        .insert({
          type: data.type,
          first_name: data.type === "private" ? data.first_name : null,
          last_name: data.type === "private" ? data.last_name : null,
          company_name: data.type !== "private" ? data.company_name : null,
          business_id: data.business_id || null,
          contact_person: data.type === "estate" ? data.contact_person : null,
          email: data.email || null,
          phone: data.phone,
          address: data.address,
          postal_code: data.postal_code,
          city: data.city,
        })
        .select()
        .single();
      if (error) throw error;
      return customer;
    },
    onSuccess: (customer) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      onCustomerCreated(customer.id);
      toast.success("Asiakas luotu onnistuneesti");
      setShowCustomerForm(false);
      setCustomerForm(INITIAL_CUSTOMER_FORM);
    },
    onError: (error: Error) => {
      toast.error("Virhe asiakkaan luonnissa", { description: error.message });
    },
  });

  // Partner mutation
  const partnerMutation = useMutation({
    mutationFn: async (data: PartnerFormData) => {
      const { data: partner, error } = await supabase
        .from("partners")
        .insert({
          type: data.type,
          name: data.name,
          business_id: data.business_id || null,
          email: data.email || null,
          phone: data.phone || null,
          address: data.address || null,
          postal_code: data.postal_code || null,
          city: data.city || null,
        })
        .select()
        .single();
      if (error) throw error;
      return partner;
    },
    onSuccess: (partner) => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      onPartnerCreated(partner.id);
      toast.success("Kumppani luotu onnistuneesti");
      setShowPartnerForm(false);
      setPartnerForm(INITIAL_PARTNER_FORM);
    },
    onError: (error: Error) => {
      toast.error("Virhe kumppanin luonnissa", { description: error.message });
    },
  });

  // Cemetery mutation
  const cemeteryMutation = useMutation({
    mutationFn: async (data: CemeteryFormData) => {
      const { data: cemetery, error } = await supabase
        .from("cemeteries")
        .insert({
          name: data.name,
          address: data.address,
          postal_code: data.postal_code,
          city: data.city,
        })
        .select()
        .single();
      if (error) throw error;
      return cemetery;
    },
    onSuccess: (cemetery) => {
      queryClient.invalidateQueries({ queryKey: ["cemeteries"] });
      onCemeteryCreated(cemetery.id);
      toast.success("Hautausmaa luotu onnistuneesti");
      setShowCemeteryForm(false);
      setCemeteryForm(INITIAL_CEMETERY_FORM);
    },
    onError: (error: Error) => {
      toast.error("Virhe hautausmaan luonnissa", { description: error.message });
    },
  });

  // Submit handlers with validation
  const submitCustomer = useCallback(() => {
    const d = customerForm;
    if (!d.phone || !d.address || !d.postal_code || !d.city) {
      toast.error("Virhe", { description: "Täytä pakolliset kentät" });
      return;
    }
    if (d.type === "private" && (!d.first_name || !d.last_name)) {
      toast.error("Virhe", { description: "Etu- ja sukunimi vaaditaan" });
      return;
    }
    if ((d.type === "company" || d.type === "municipality") && !d.company_name) {
      toast.error("Virhe", { description: "Yrityksen nimi vaaditaan" });
      return;
    }
    if (d.type === "estate" && (!d.company_name || !d.contact_person)) {
      toast.error("Virhe", { description: "Kuolinpesän nimi ja yhteyshenkilö vaaditaan" });
      return;
    }
    customerMutation.mutate(customerForm);
  }, [customerForm, customerMutation]);

  const submitPartner = useCallback(() => {
    if (!partnerForm.name) {
      toast.error("Virhe", { description: "Kumppanin nimi vaaditaan" });
      return;
    }
    partnerMutation.mutate(partnerForm);
  }, [partnerForm, partnerMutation]);

  const submitCemetery = useCallback(() => {
    const d = cemeteryForm;
    if (!d.name || !d.address || !d.postal_code || !d.city) {
      toast.error("Virhe", { description: "Täytä kaikki pakolliset kentät" });
      return;
    }
    cemeteryMutation.mutate(cemeteryForm);
  }, [cemeteryForm, cemeteryMutation]);

  // Cancel handlers
  const cancelCustomer = useCallback(() => {
    setShowCustomerForm(false);
    setCustomerForm(INITIAL_CUSTOMER_FORM);
  }, []);

  const cancelPartner = useCallback(() => {
    setShowPartnerForm(false);
    setPartnerForm(INITIAL_PARTNER_FORM);
  }, []);

  const cancelCemetery = useCallback(() => {
    setShowCemeteryForm(false);
    setCemeteryForm(INITIAL_CEMETERY_FORM);
  }, []);

  // Reset all
  const resetAll = useCallback(() => {
    setShowCustomerForm(false);
    setShowPartnerForm(false);
    setShowCemeteryForm(false);
    setCustomerForm(INITIAL_CUSTOMER_FORM);
    setPartnerForm(INITIAL_PARTNER_FORM);
    setCemeteryForm(INITIAL_CEMETERY_FORM);
  }, []);

  return {
    // Customer
    showCustomerForm,
    setShowCustomerForm,
    customerForm,
    setCustomerForm,
    submitCustomer,
    cancelCustomer,
    customerPending: customerMutation.isPending,

    // Partner
    showPartnerForm,
    setShowPartnerForm,
    partnerForm,
    setPartnerForm,
    submitPartner,
    cancelPartner,
    partnerPending: partnerMutation.isPending,

    // Cemetery
    showCemeteryForm,
    setShowCemeteryForm,
    cemeteryForm,
    setCemeteryForm,
    submitCemetery,
    cancelCemetery,
    cemeteryPending: cemeteryMutation.isPending,

    // Reset
    resetAll,
  };
}
